/**
 * Overtime Pending Content (Tab İçeriği)
 * 
 * Bekleyen fazla mesai taleplerini gösteren tab içeriği.
 * OvertimeManagement sayfasında tab olarak kullanılır.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message, DatePicker, Input, Spin, Empty, Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/tr';
import locale from 'antd/lib/date-picker/locale/tr_TR';

// Services
import overtimeService from '../../../../services/overtime/overtime.service';
import { useDepartments } from '../../../../hooks/useDepartments';

// Types
import { OvertimeDto, OvertimeFilters } from '../../../../types/overtime.types';

// Components
import OvertimeCard from './OvertimeCard';
import OvertimeApprovalModal from './OvertimeApprovalModal';
import OvertimeRejectModal from './OvertimeRejectModal';
import CommonSelect from '../../../../core/common/commonSelect';

// Utils
import { handleApiError } from '../../../../utils/errorHandler';
import { OVERTIME_TOAST_MESSAGES } from '../../../../constants/overtimeConstants';

const { RangePicker } = DatePicker;
const { Search } = Input;

/**
 * Bekleyen Talepler Tab İçeriği
 */
const OvertimePendingContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { departments } = useDepartments();

  // State'ler
  const [overtimes, setOvertimes] = useState<OvertimeDto[]>([]);
  const [filteredOvertimes, setFilteredOvertimes] = useState<OvertimeDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  
  // Filtreler
  const [departmentFilter, setDepartmentFilter] = useState<number>(0); // 0 = Tüm Departmanlar
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  
  // Modaller
  const [approvalModalVisible, setApprovalModalVisible] = useState<boolean>(false);
  const [rejectModalVisible, setRejectModalVisible] = useState<boolean>(false);
  const [selectedOvertime, setSelectedOvertime] = useState<OvertimeDto | null>(null);

  /**
   * Bekleyen talepleri getir
   */
  const fetchPendingOvertimes = useCallback(async () => {
    setLoading(true);
    try {
      const filters: OvertimeFilters = {
        departmentId: departmentFilter > 0 ? departmentFilter : undefined,
        startDate: dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined,
        endDate: dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      const result = await overtimeService.getPendingOvertimes(filters);
      setOvertimes(result);
      setFilteredOvertimes(result);
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.message?.includes('404')) {
        message.warning(t('overtime.endpointNotReady', 'Fazla mesai sistemi backend\'de henüz aktif değil'));
      } else {
        handleApiError(error, t);
      }
    } finally {
      setLoading(false);
    }
  }, [departmentFilter, dateRange, t]);

  // Filtreler değiştiğinde verileri getir
  useEffect(() => {
    fetchPendingOvertimes();
  }, [fetchPendingOvertimes]);

  /**
   * Arama filtresi
   */
  useEffect(() => {
    if (!searchText) {
      setFilteredOvertimes(overtimes);
      return;
    }

    const filtered = overtimes.filter(overtime => {
      const fullName = `${overtime.user.firstName} ${overtime.user.lastName}`.toLowerCase();
      const email = overtime.user.email?.toLowerCase() || '';
      const description = overtime.description?.toLowerCase() || '';
      const search = searchText.toLowerCase();

      return fullName.includes(search) || email.includes(search) || description.includes(search);
    });

    setFilteredOvertimes(filtered);
  }, [searchText, overtimes]);

  /**
   * Onay modalını aç
   */
  const handleApprove = (overtime: OvertimeDto) => {
    setSelectedOvertime(overtime);
    setApprovalModalVisible(true);
  };

  /**
   * Red modalını aç
   */
  const handleReject = (overtime: OvertimeDto) => {
    setSelectedOvertime(overtime);
    setRejectModalVisible(true);
  };

  /**
   * Onaylama işlemi
   */
  const handleConfirmApproval = async () => {
    if (!selectedOvertime) return;

    setActionLoading(true);
    try {
      await overtimeService.reviewOvertime(selectedOvertime.id, {
        action: 'approve',
      });

      message.success(OVERTIME_TOAST_MESSAGES.approveSuccess);
      setApprovalModalVisible(false);
      setSelectedOvertime(null);
      fetchPendingOvertimes();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Reddetme işlemi
   */
  const handleConfirmRejection = async (overtimeId: number, rejectionReason: string) => {
    setActionLoading(true);
    try {
      await overtimeService.reviewOvertime(overtimeId, {
        action: 'reject',
        rejectionReason,
      });

      message.success(OVERTIME_TOAST_MESSAGES.rejectSuccess);
      setRejectModalVisible(false);
      setSelectedOvertime(null);
      fetchPendingOvertimes();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setActionLoading(false);
    }
  };

  // Departman seçenekleri
  const departmentOptions = [
    { value: 0, label: t('overtime.allDepartments', 'Tüm Departmanlar') },
    ...departments.map(dept => ({
      value: dept.id!,
      label: dept.name
    }))
  ];

  return (
    <div className="overtime-pending-content">
      {/* Filtreler Card */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* Departman Filtresi */}
            <div className="col-lg-3 col-md-6 col-sm-12">
              <label className="form-label fw-medium">
                <i className="ti ti-building me-2" />
                {t('overtime.filters.department', 'Departman')}
              </label>
              <CommonSelect
                options={departmentOptions}
                value={departmentOptions.find(opt => opt.value === departmentFilter)}
                onChange={(selected: any) => setDepartmentFilter(selected?.value)}
                className="select"
              />
            </div>

            {/* Tarih Aralığı Filtresi */}
            <div className="col-lg-3 col-md-6 col-sm-12">
              <label className="form-label fw-medium">
                <i className="ti ti-calendar me-2" />
                {t('overtime.filters.dateRange', 'Tarih Aralığı')}
              </label>
              <div style={{ width: '100%' }}>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
                  format="DD/MM/YYYY"
                  locale={i18n.language === 'tr' ? locale : undefined}
                  style={{ width: '100%', height: '38px' }}
                  placeholder={[
                    t('overtime.filters.startDate', 'Başlangıç'),
                    t('overtime.filters.endDate', 'Bitiş')
                  ]}
                />
              </div>
            </div>

            {/* Arama */}
            <div className="col-lg-4 col-md-6 col-sm-12">
              <label className="form-label fw-medium">
                <i className="ti ti-search me-2" />
                {t('overtime.filters.search', 'Ara')}
              </label>
              <div style={{ width: '100%' }}>
                <Search
                  placeholder={t('overtime.filters.searchPlaceholder', 'Çalışan adı, e-posta veya açıklama...')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  style={{ height: '38px' }}
                />
              </div>
            </div>

            {/* Yenile Butonu */}
            <div className="col-lg-2 col-md-6 col-sm-12">
              <label className="form-label fw-medium d-none d-lg-block">&nbsp;</label>
              <div style={{ width: '100%' }}>
                <Button
                  icon={<i className="ti ti-reload me-2" />}
                  onClick={fetchPendingOvertimes}
                  loading={loading}
                  block
                  style={{ height: '38px', backgroundColor: '#f8f9fa', borderColor: '#dee2e6', color: '#6c757d' }}
                >
                  {t('common.buttons.refresh', 'Yenile')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* İçerik */}
      {loading ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <Spin size="large" />
            <p className="mt-3 text-muted">{t('overtime.loading', 'Yükleniyor...')}</p>
          </div>
        </div>
      ) : filteredOvertimes.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="mb-3">
              <i className="ti ti-clock-off" style={{ fontSize: '64px', color: '#d9d9d9' }} />
            </div>
            <h5 className="mb-2">
              {searchText || departmentFilter > 0 || dateRange
                ? t('overtime.noFilteredResults', 'Filtre kriterlerine uygun talep bulunamadı')
                : t('overtime.noPendingRequests', 'Bekleyen fazla mesai talebi bulunmuyor')}
            </h5>
            <p className="text-muted mb-0">
              {t('overtime.noPendingHint', 'Yeni talepler geldiğinde burada görüntülenecek')}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="row g-3">
            {filteredOvertimes.map(overtime => (
              <div key={overtime.id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12">
                <OvertimeCard
                  overtime={overtime}
                  onApprove={() => handleApprove(overtime)}
                  onReject={() => handleReject(overtime)}
                />
              </div>
            ))}
          </div>

          {/* Sonuç Sayısı */}
          <div className="card mt-3">
            <div className="card-body py-2">
              <div className="d-flex align-items-center">
                <i className="ti ti-info-circle me-2 text-primary" style={{ fontSize: '18px' }} />
                <span className="text-muted fw-medium">
                  {t('overtime.totalPending', 'Toplam {{count}} bekleyen talep gösteriliyor', {
                    count: filteredOvertimes.length
                  })}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modaller */}
      {selectedOvertime && (
        <>
          <OvertimeApprovalModal
            visible={approvalModalVisible}
            overtime={selectedOvertime}
            onConfirm={handleConfirmApproval}
            onCancel={() => {
              setApprovalModalVisible(false);
              setSelectedOvertime(null);
            }}
            loading={actionLoading}
          />

          <OvertimeRejectModal
            visible={rejectModalVisible}
            overtime={selectedOvertime}
            onConfirm={handleConfirmRejection}
            onCancel={() => {
              setRejectModalVisible(false);
              setSelectedOvertime(null);
            }}
            loading={actionLoading}
          />
        </>
      )}
    </div>
  );
};

export default OvertimePendingContent;

